import React, { useState, useEffect } from "react";
import { Input, Select, Pagination } from "@multiagentbook/ui";
import AlertTriangle from "lucide-react/icons/alert-triangle";
import { Usecase } from "./types";
import UsecaseDetail from "./detail";
import UsecaseCard from "./card";


interface UsecaseHomeProps {
  baseUrl: string;
}

const ITEMS_PER_PAGE = 20;

const createSlug = (title: string) => {
  return title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
};

const UsecaseHome: React.FC<UsecaseHomeProps> = ({ baseUrl }) => {
  const [usecases, setUsecases] = useState<Usecase[]>([]);
  const [filteredUsecases, setFilteredUsecases] = useState<Usecase[]>([]);
  const [selectedUsecase, setSelectedUsecase] = useState<Usecase | null>(null);
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState<string[]>([]);
  const [frameworks, setFrameworks] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [totalImplementations, setTotalImplementations] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter states
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    const fetchUsecases = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${baseUrl}/samples/usecases.json?nonce=${Date.now()}`
        );
        const data = await response.json();
        const usecaseArray = Object.values(data.usecases) as Usecase[];

        // Calculate total implementations
        const implCount = usecaseArray.reduce(
          (acc, usecase) => acc + usecase.implementations.length,
          0
        );
        setTotalImplementations(implCount);

        setUsecases(usecaseArray);
        setFilteredUsecases(usecaseArray);

        // Extract unique domains from all usecases
        const allDomains = new Set(usecaseArray.flatMap((u) => u.domains));
        setDomains(Array.from(allDomains));

        // Extract unique frameworks from all implementations
        const allFrameworks = new Set(
          usecaseArray.flatMap((u) =>
            u.implementations.map((impl) => impl.framework)
          )
        );
        setFrameworks(Array.from(allFrameworks));

        // Extract unique tags
        const allTags = new Set(usecaseArray.flatMap((u) => u.tags));
        setTags(Array.from(allTags));

        // Check URL for initial usecase
        const urlParams = new URLSearchParams(window.location.search);
        const usecaseSlug = urlParams.get("usecase");
        if (usecaseSlug) {
          const usecase = usecaseArray.find(
            (u) => createSlug(u.title) === usecaseSlug
          );
          if (usecase) {
            setSelectedUsecase(usecase);
          }
        }
      } catch (error) {
        console.error("Error fetching usecases:", error);
        setUsecases([]);
        setFilteredUsecases([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsecases();
  }, [baseUrl]);

  useEffect(() => {
    let filtered = [...usecases];

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((usecase) => {
        const searchableFields = [
          usecase.title,
          usecase.description,
          ...usecase.domains,
          ...usecase.tags,
          ...usecase.implementations.map((i) => i.title),
          ...usecase.implementations.map((i) => i.description),
          ...usecase.implementations.flatMap(
            (i) => i.code.metadata?.tags || []
          ),
          ...usecase.implementations.flatMap(
            (i) => i.code.metadata?.domains || []
          ),
        ];
        return searchableFields.some((field) =>
          field?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (tagFilter.length > 0) {
      filtered = filtered.filter((usecase) =>
        tagFilter.some((tag) => usecase.tags.includes(tag))
      );
    }

    setFilteredUsecases(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [usecases, tagFilter, searchText]);

  const handleUsecaseClick = (usecase: Usecase) => {
    const slug = createSlug(usecase.title);
    window.history.pushState(null, "", `/labs/usecases/?usecase=${slug}`);
    setSelectedUsecase(usecase);
  };

  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredUsecases.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  return (
    <div className="mb-6">
      {selectedUsecase ? (
        <UsecaseDetail
          usecase={selectedUsecase}
          onBack={() => {
            window.history.pushState(null, "", "/labs/usecases/");
            setSelectedUsecase(null);
          }}
          baseUrl={baseUrl}
        />
      ) : (
        <>
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              {/* <h1 className="text-3xl font-bold">Multi-Agent Usecases</h1> */}
              <span className="bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded text-sm font-medium">
                {totalImplementations} implementations
              </span>
            </div>
            <p className="text-secondary">
              Explore real-world examples of multi-agent systems across
              different frameworks and domains.
            </p>
          </div>

          <div className="mb-6">
            <Input
              placeholder="Search usecases..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full mb-4"
            />
            <div className="flex flex-wrap gap-2 items-center">
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => {
                    const isSelected = tagFilter.includes(tag);
                    if (isSelected) {
                      setTagFilter(tagFilter.filter(t => t !== tag));
                    } else {
                      setTagFilter([...tagFilter, tag]);
                    }
                  }}
                  className={`px-3 py-1 text-xs rounded border transition-colors ${
                    tagFilter.includes(tag)
                      ? 'bg-accent text-white border-accent'
                      : 'bg-primary text-secondary border-secondary hover:border-accent hover:text-accent'
                  }`}
                >
                  {tag}
                </button>
              ))}
              {tagFilter.length > 0 && (
                <span className="text-xs text-gray-500 ml-2">
                  {tagFilter.length} tag{tagFilter.length !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <p>Loading usecases...</p>
              </div>
            </div>
          ) : filteredUsecases.length === 0 ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto" />
                <p className="text-secondary mt-2">
                  No usecases match your filters
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getCurrentPageItems().map((usecase, index) => (
                  <UsecaseCard
                    key={`${usecase.title}-${index}`}
                    usecase={usecase}
                    onClick={handleUsecaseClick}
                    index={index}
                  />
                ))}
              </div>

              <div className="flex justify-center mt-8">
                <Pagination
                  current={currentPage}
                  total={filteredUsecases.length}
                  pageSize={ITEMS_PER_PAGE}
                  onChange={(page) => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default UsecaseHome;
